import logger from '../utils/logger';
import { MarkerType } from '@xyflow/react';
import { useDispatch, useSelector } from 'react-redux';
import api from '../api/api';
import CustomAlert from '../components/CustomAlert';
import { getSwimlaneApiByMode } from '../services/swimlaneApiMode';
import { getProcessDates } from '../utils/getProcessDates';
import { setMapData } from '../redux/mapDataSlice';
import { useTranslation } from './useTranslation';

export const useFetchNodes = ({
  getLevelKey,
  currentLevel,
  currentParentId,
  LoginUser,
  id,
  setNodes,
  setEdges,
  setUser,
  setters = {},
  mode = 'edit', // "edit" | "draft" | "publish"
  handleLabelChange,
}) => {
  const dispatch = useDispatch();
  const t = useTranslation();
  const mapDataCache = useSelector((state) => state.mapData.cache);

  const fetchNodes = async (language_id = null, forceRefresh = false) => {
    try {
      const levelParam = getLevelKey(currentLevel, currentParentId);
      const user_id = LoginUser ? LoginUser.id : null;
      const Process_id = id ? id : null;

      // Persist and resolve language selection
      if (language_id && Process_id) {
        sessionStorage.setItem(`process_lang_id_${Process_id}`, language_id);
      }
      const savedLangId = Process_id
        ? sessionStorage.getItem(`process_lang_id_${Process_id}`)
        : null;
      const finalLanguageId = language_id || savedLangId || null;

      // Generate Cache Key
      const cacheKey = `${Process_id}_${levelParam}_${finalLanguageId || 'def'}_${mode}`;

      // Check Cache
      if (!forceRefresh && mapDataCache[cacheKey]) {
        const cachedData = mapDataCache[cacheKey];
        // Apply cached data (same logic as below but with cached object)
        applyData(cachedData);
        return;
      }

      const apiCaller = getSwimlaneApiByMode(api, mode);

      const data = await apiCaller([
        levelParam,
        parseInt(user_id),
        Process_id,
        currentParentId,
        finalLanguageId,
        mode === 'edit' ? 'Editmode' : undefined,
      ]);

      // Save to Cache
      // Note: We're caching the raw API response *before* node processing,
      // or we can cache the processed nodes.
      // Caching the API response allows us to re-run node processing logic if needed (e.g. label change handlers).
      // However, node processing involves async checks.

      // Let's proceed with processing, then cache the result?
      // Actually, standard Redux pattern is to store serializable data.
      // The API response is serializable.

      if (data) {
        dispatch(setMapData({ key: cacheKey, data }));
        await applyData(data);
      }
    } catch (error) {
      logger.error('Error fetching nodes:', error);
      CustomAlert.error(
        t('error') || 'Error',
        t('failed_to_fetch_nodes_please_refresh_this_page') ||
          'Failed to fetch nodes. Please refresh this page.'
      );
    }
  };

  const applyData = async (data) => {
    const Process_id = id ? id : null;
    // =============================
    // MODE BASED API
    // =============================
    // let data = null;

    // if (mode === "edit") {
    //   data = await api.getNodes(levelParam, parseInt(user_id), Process_id, currentParentId, language_id, "Editmode");
    // }

    // if (mode === "draft") {
    //   data = await api.getNodes(levelParam, parseInt(user_id), Process_id, currentParentId, language_id);
    // }

    // if (mode === "publish") {
    //   data = await api.getPublishedNodes(levelParam, parseInt(user_id), Process_id, currentParentId, language_id);
    // }

    // =============================
    // COMMON SETTER VALUES
    // =============================
    const {
      Settitle,
      SetTitleTranslation,
      setprocess_img,
      setprocessDefaultlanguage_id,
      setOriginalDefaultlanguge_id,
      setSupportedLanguages,

      SetParentPageGroupId,
      setgetPublishedDate,
      setDraftedDate,
    } = setters;

    if (Process_id && data?.nodes?.[0]?.page_group_id) {
      const dates = await getProcessDates(Process_id, data?.nodes[0].page_group_id);
      setgetPublishedDate?.(dates.publishedDate);
      setDraftedDate?.(dates.draftDate);
    }

    if (data?.user_id) {
      setUser({
        id: data.actual_user_id,
        type: data.type || 'self',
        role: data.role || 'self',
        OwnId: data.user_id,
        actual_user_id: data.actual_user_id,
      });
    }

    // =============================
    // SET BASIC PROCESS DATA
    // =============================
    Settitle?.(data.title);
    SetTitleTranslation?.(data.TitleTranslation);
    setprocess_img?.(data.process_img);
    setprocessDefaultlanguage_id?.(data.processDefaultlanguage_id);
    setOriginalDefaultlanguge_id?.(data.OriginalDefaultlanguge_id);
    setSupportedLanguages?.(data.ProcessSupportLanguage || []);

    SetParentPageGroupId?.(data.PageGroupId);

    // =============================
    // NODES PROCESS
    // =============================
    const parsedNodes = await Promise.all(
      (data.nodes || [])
        .filter((node) => (mode === 'publish' ? node.type !== 'StickyNote' : true))
        .map(async (node) => {
          const parsedData = JSON.parse(node.data);
          const parsedPosition = JSON.parse(node.position);
          const parsedMeasured = JSON.parse(node.measured);

          // NEXT LEVEL CHECK
          const newLevel = currentLevel + 1;
          const levelParam =
            node.node_id !== null ? `level${newLevel}_${node.node_id}` : `level${currentLevel}`;

          let hasNextLevel = false;
          try {
            const check =
              mode === 'publish'
                ? await api.checkPublishRecord(levelParam, Process_id)
                : await api.checkRecord(levelParam, Process_id);

            hasNextLevel = check?.status === true;
          } catch (e) {
            logger.error('check error', e);
          }

          return {
            ...node,
            data: {
              ...parsedData,
              onLabelChange: (newLabel) => handleLabelChange(node.node_id, newLabel),
              width_height: parsedMeasured,
              node_id: node.node_id,
              LinkToStatus: node.LinkToStatus,
              hasNextLevel,
            },
            type: node.type,
            id: node.node_id,
            measured: parsedMeasured,
            position: parsedPosition,
            draggable: mode === 'edit',
          };
        })
    );

    const parsedEdges = (data.edges || []).map((edge) => ({
      ...edge,
      animated: Boolean(edge.animated),
      markerEnd: { type: MarkerType.ArrowClosed },
      style: { stroke: '#002060', strokeWidth: 2 },
      type: 'step',
    }));

    setNodes(parsedNodes);
    setEdges(parsedEdges);
  };
  // Close applyData function and return from hook
  return { fetchNodes };
};
